// src/pages/Videos.tsx - Horizontal full-width card layout "My Reviews" page
import React, { useEffect, useState, useRef } from 'react';
import { Button, Tag, Space, message, Modal, Input, Progress, Empty, Tooltip, Spin } from 'antd';
import {
  DownloadOutlined,
  PlayCircleOutlined,
  ShareAltOutlined,
  FilePdfOutlined,
  SearchOutlined,
  VideoCameraOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  CopyOutlined,
  BarChartOutlined,
  ReadOutlined,
} from '@ant-design/icons';
import VideoPlayer from '../components/VideoPlayer';
import { ShareModal } from '../components/ShareModal';
import { ShareAnalyticsDrawer } from '../components/ShareAnalyticsDrawer';
import { PDFViewerModal } from '../components/pdf-viewer';
import { authenticatedFetch } from '../providers/authProvider';

const { Search } = Input;

interface VideoJob {
  job_id: string;
  original_filename: string;
  extracted_address?: string;  // v1.2: Auto-extracted property address
  created_at: any;
  status: string;
  planType: string;
  generation_type?: string;
  thumbnail_url?: string;
  shares?: Array<{
    share_token: string;
    recipients: string[];
    view_count: number;
    created_at: any;
  }>;
  tasks: {
    create_script?: {
      status: string;
      script_text?: string;
    };
    extract_images?: {
      status: string;
    };
    create_narration?: {
      status: string;
    };
    create_video: {
      status: string;
      video_url?: string;
      gcs_path?: string;
      file_size?: number;
      error?: string;
      render_id?: string;
    };
  };
}

// Helper to get display title - prefer extracted_address over filename
const getDisplayTitle = (job: VideoJob): string => {
  if (job.extracted_address) {
    return job.extracted_address;
  }
  if (job.original_filename) {
    return job.original_filename.replace('.pdf', '');
  }
  return `Video-${job.job_id.substring(0, 8)}`;
};

const Videos: React.FC = () => {
  const [videos, setVideos] = useState<VideoJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [shareModalVisible, setShareModalVisible] = useState(false);
  const [playerModalVisible, setPlayerModalVisible] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<VideoJob | null>(null);
  const [searchText, setSearchText] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [analyticsToken, setAnalyticsToken] = useState<string | null>(null);
  const [transcriptJob, setTranscriptJob] = useState<VideoJob | null>(null);
  const mountedRef = useRef(true);
  const fetchAttemptRef = useRef(0);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // PDF Viewer state
  const [pdfViewerState, setPdfViewerState] = useState<{
    visible: boolean;
    pdfUrl: string;
    documentId: string;
    documentTitle: string;
  }>({ visible: false, pdfUrl: '', documentId: '', documentTitle: '' });

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    fetchUserVideos();

    // Set up polling for processing videos
    pollingIntervalRef.current = setInterval(() => {
      if (mountedRef.current) {
        fetchUserVideos(true); // Silent fetch for polling
      }
    }, 5000);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  const fetchUserVideos = async (isPolling = false) => {
    if (!mountedRef.current) return;

    try {
      if (!isPolling) {
        setLoading(true);
      }

      const response = await authenticatedFetch('/user-jobs');

      if (!response.ok) {
        throw new Error('Failed to fetch videos');
      }

      const data = await response.json();

      // Filter for video jobs (not script-only)
      const videoJobs = data.jobs?.filter((job: VideoJob) =>
        job.generation_type !== 'script_only'
      ) || [];

      if (mountedRef.current) {
        setVideos(videoJobs);
        fetchAttemptRef.current = 0; // Reset attempts on success
      }
    } catch (error) {
      // Only log errors after all retries have failed and not during polling
      if (fetchAttemptRef.current >= 2 && !isPolling) {
        console.error('Error fetching videos:', error);
      }

      // Retry if it's likely an auth timing issue (on initial load)
      if (fetchAttemptRef.current < 3 && mountedRef.current && !isPolling) {
        fetchAttemptRef.current++;
        setTimeout(() => {
          if (mountedRef.current) {
            fetchUserVideos();
          }
        }, 1000); // Wait 1 second before retry
      } else if (mountedRef.current && fetchAttemptRef.current >= 3 && !isPolling) {
        message.error('Failed to fetch videos');
      }
    } finally {
      if (mountedRef.current && !isPolling) {
        setLoading(false);
      }
    }
  };

  const handleViewVideo = async (job: VideoJob) => {
    const videoUrl = job.tasks?.create_video?.video_url;

    if (videoUrl) {
      // Open in modal player
      setSelectedVideo(job);
      setPlayerModalVisible(true);
    } else {
      // Need to get signed URL for private access
      try {
        const response = await authenticatedFetch(`/get-video-url/${job.job_id}`);

        if (response.ok) {
          const data = await response.json();
          // Update job with signed URL and open player
          const updatedJob = { ...job };
          updatedJob.tasks.create_video.video_url = data.video_url;
          setSelectedVideo(updatedJob);
          setPlayerModalVisible(true);
        } else {
          message.error('Video not available');
        }
      } catch (error) {
        console.error('Error getting video URL:', error);
        message.error('Failed to access video');
      }
    }
  };

  const handleDownload = async (job: VideoJob) => {
    const videoUrl = job.tasks?.create_video?.video_url;

    if (videoUrl) {
      window.open(videoUrl, '_blank');
      message.success('Video opened in new tab - right-click to save');
    } else {
      message.error('Video not available for download');
    }
  };

  const handleShare = (job: VideoJob) => {
    const videoUrl = job.tasks?.create_video?.video_url;
    if (!videoUrl) {
      message.error('Video is not ready to share yet');
      return;
    }
    setSelectedVideo(job);
    setShareModalVisible(true);
  };

  const handleCopyLink = (job: VideoJob) => {
    // Get the first active share token
    const activeShare = job.shares?.find(share => share.share_token);

    if (!activeShare) {
      message.error('No active share link found');
      return;
    }

    const shareUrl = `https://app.spectreviews.com/share/${activeShare.share_token}`;

    // Copy to clipboard
    navigator.clipboard.writeText(shareUrl).then(
      () => {
        message.success('Link copied!');
      },
      () => {
        message.error('Failed to copy link');
      }
    );
  };

  const handleViewPDF = async (job: VideoJob) => {
    try {
      // Fetch PDF as blob through authenticated proxy to avoid CORS issues
      const response = await authenticatedFetch(`/proxy-pdf/${job.job_id}`);

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to load PDF' }));
        throw new Error(error.error || 'Failed to load PDF');
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      // Open PDF viewer modal
      setPdfViewerState({
        visible: true,
        pdfUrl: blobUrl,
        documentId: job.job_id,
        documentTitle: getDisplayTitle(job),
      });
    } catch (error: any) {
      console.error('Error getting PDF:', error);
      message.error(error.message || 'Failed to load PDF');
    }
  };

  const handleDownloadTranscriptDocx = (job: VideoJob) => {
    const scriptText = job.tasks?.create_script?.script_text;
    if (!scriptText) return;

    const title = getDisplayTitle(job);
    // Parse sections from script text (format: [SectionName:PageNum]\nContent)
    const sections = scriptText.split(/\[([^\]]+)\]/).filter(Boolean);
    let htmlSections = '';
    for (let i = 0; i < sections.length; i += 2) {
      const heading = sections[i]?.split(':')[0]?.trim() || '';
      const content = sections[i + 1]?.trim() || '';
      if (heading && content) {
        htmlSections += `<h2>${heading}</h2><p>${content.replace(/\n/g, '<br/>')}</p>`;
      }
    }

    // Word-compatible HTML document
    const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word"><head><meta charset="utf-8"><style>body{font-family:'Times New Roman',serif;font-size:12pt;margin:1in}h1{font-size:16pt;text-align:center}h2{font-size:13pt;margin-top:18pt;color:#333}p{line-height:1.6}</style></head><body><h1>${title}</h1><p style="text-align:center;color:#666;font-size:10pt">Inspection Review Transcript</p>${htmlSections}</body></html>`;

    const blob = new Blob([html], { type: 'application/vnd.ms-word' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title.replace(/[^a-zA-Z0-9 ]/g, '').replace(/\s+/g, '_')}_Transcript.doc`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    message.success('Transcript downloaded');
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'Unknown';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED': return 'green';
      case 'PROCESSING': return 'blue';
      case 'RENDERING': return 'orange';
      case 'FAILED': return 'red';
      default: return 'default';
    }
  };

  const getOverallProgress = (job: VideoJob) => {
    const tasks = ['create_script', 'extract_images', 'create_narration', 'create_video'];
    const completedTasks = tasks.filter(task => {
      const taskData = job.tasks[task as keyof typeof job.tasks];
      return taskData?.status === 'COMPLETED' || taskData?.status === 'APPROVED';
    }).length;

    return Math.round((completedTasks / tasks.length) * 100);
  };

  const filteredVideos = videos.filter(video => {
    if (!searchText) return true;

    const searchLower = searchText.toLowerCase();
    const filename = video.original_filename?.toLowerCase() || '';
    const address = video.extracted_address?.toLowerCase() || '';

    return filename.includes(searchLower) || address.includes(searchLower);
  });

  // Horizontal Card Component
  const VideoCard = ({ job }: { job: VideoJob }) => {
    const isCompleted = job.tasks?.create_video?.status === 'COMPLETED';
    const hasVideoUrl = !!job.tasks?.create_video?.video_url;
    const isProcessing = job.status === 'PROCESSING' || job.tasks?.create_video?.status === 'RENDERING';
    const videoStatus = job.tasks?.create_video?.status || job.status;
    const dateObj = job.created_at?.seconds ? new Date(job.created_at.seconds * 1000) : new Date(job.created_at);
    const hasShares = (job.shares?.length || 0) > 0;
    const totalViews = job.shares?.reduce((sum, share) => sum + (share.view_count || 0), 0) || 0;

    return (
      <div
        style={{
          width: '100%',
          backgroundColor: '#ffffff',
          border: '1px solid #e5e7eb',
          borderRadius: '12px',
          padding: '16px',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: '16px',
          transition: 'box-shadow 0.2s',
          cursor: 'default',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.06)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        {/* LEFT: Thumbnail (clicking opens the PDF viewer, same as "Open Review" button) */}
        <div
          style={{
            flexShrink: 0,
            width: isMobile ? '100%' : '140px',
            // On mobile use a 16:9 aspect ratio so the thumbnail scales with
            // viewport width instead of stretching/squashing at a fixed 180px.
            aspectRatio: isMobile ? '16 / 9' : undefined,
            height: isMobile ? undefined : '180px',
            backgroundColor: '#f3f4f6',
            borderRadius: '8px',
            overflow: 'hidden',
            position: 'relative',
            cursor: 'pointer',
            transition: 'opacity 0.15s',
          }}
          onClick={() => handleViewPDF(job)}
          onMouseEnter={(e) => {
            e.currentTarget.style.opacity = '0.92';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.opacity = '1';
          }}
        >
          {job.thumbnail_url ? (
            <img
              src={job.thumbnail_url}
              alt={getDisplayTitle(job)}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                imageRendering: 'auto',
                backgroundColor: '#ffffff',
              }}
            />
          ) : isProcessing ? (
            <div
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
            >
              <Spin indicator={<ClockCircleOutlined spin style={{ fontSize: 32, color: '#3b82f6' }} />} />
              <div style={{ fontSize: '18px', fontWeight: 600, color: '#3b82f6' }}>
                {getOverallProgress(job)}%
              </div>
            </div>
          ) : (
            <div
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              }}
            >
              <VideoCameraOutlined style={{ fontSize: 48, color: '#fff', opacity: 0.8 }} />
            </div>
          )}
        </div>

        {/* MIDDLE: Info Section */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px', minWidth: 0 }}>
          {/* Title — clamps to 2 lines on mobile so long addresses don't get
              cut off mid-street. Single line + ellipsis on desktop. */}
          <div
            style={{
              fontSize: isMobile ? '16px' : '15px',
              fontWeight: 600,
              color: '#1f2937',
              overflow: 'hidden',
              ...(isMobile
                ? {
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical' as const,
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                  }
                : {
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap' as const,
                  }),
            }}
            title={getDisplayTitle(job)}
          >
            {getDisplayTitle(job)}
          </div>

          {/* Date */}
          <div style={{ fontSize: '13px', color: '#6b7280' }}>
            {dateObj.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
            })}
          </div>

          {/* Tags row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <Tag
              color={getStatusColor(videoStatus)}
              style={{ borderRadius: '12px', fontSize: '12px', margin: 0 }}
            >
              {videoStatus === 'RENDERING' ? (
                <span><ClockCircleOutlined spin /> RENDERING</span>
              ) : (
                videoStatus
              )}
            </Tag>
            {hasShares && (
              <Tag
                color="blue"
                style={{ borderRadius: '12px', fontSize: '12px', margin: 0 }}
              >
                Shared
              </Tag>
            )}
          </div>

          {/* Progress bar for processing jobs */}
          {isProcessing && (
            <div style={{ marginTop: '4px' }}>
              <Progress
                percent={getOverallProgress(job)}
                size="small"
                status="active"
                strokeColor="#3b82f6"
              />
            </div>
          )}

          {/* Share info */}
          {hasShares && job.shares && job.shares.length > 0 && (
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
              <div style={{ marginBottom: '2px' }}>
                <strong>Shared with:</strong>{' '}
                {job.shares[0].recipients.slice(0, 2).join(', ')}
                {job.shares[0].recipients.length > 2 && ` +${job.shares[0].recipients.length - 2} more`}
              </div>
              {totalViews > 0 && (
                <div>
                  <strong>{totalViews}</strong> {totalViews === 1 ? 'view' : 'views'}
                </div>
              )}
            </div>
          )}
        </div>

        {/* RIGHT: Actions
            Mobile layout: 2-column grid for the three primary buttons (so they
            fit on one tap target each at common phone widths) plus the 4 icon
            buttons in a row underneath. Desktop: vertical column at 140px. */}
        <div
          style={{
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            alignItems: 'stretch',
            width: isMobile ? '100%' : '140px',
          }}
        >
          {isMobile ? (
            <>
              {/* Primary CTA (full width) */}
              <Button
                type="primary"
                icon={<FilePdfOutlined />}
                onClick={() => handleViewPDF(job)}
                size="middle"
                style={{ width: '100%' }}
              >
                Open Review
              </Button>

              {/* Secondary actions in a 2-column grid */}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '8px',
                }}
              >
                <Button
                  icon={<PlayCircleOutlined />}
                  onClick={() => handleViewVideo(job)}
                  disabled={!isCompleted}
                >
                  Watch
                </Button>
                <Button
                  icon={<ShareAltOutlined />}
                  onClick={() => handleShare(job)}
                  disabled={!isCompleted || !hasVideoUrl}
                >
                  Share
                </Button>
              </div>
            </>
          ) : (
            <>
              <Button
                type="primary"
                icon={<ShareAltOutlined />}
                onClick={() => handleShare(job)}
                disabled={!isCompleted || !hasVideoUrl}
                style={{ width: '100%' }}
              >
                Share Review
              </Button>
              <Button
                icon={<FilePdfOutlined />}
                onClick={() => handleViewPDF(job)}
                style={{ width: '100%' }}
              >
                Open Review
              </Button>
              <Button
                icon={<PlayCircleOutlined />}
                onClick={() => handleViewVideo(job)}
                disabled={!isCompleted}
                style={{ width: '100%' }}
              >
                Watch Video
              </Button>
            </>
          )}

          {/* Icon buttons row */}
          <div
            style={{
              display: 'flex',
              gap: '8px',
              width: '100%',
            }}
          >
            <Tooltip title="Download">
              <Button
                icon={<DownloadOutlined />}
                onClick={() => handleDownload(job)}
                disabled={!isCompleted || !hasVideoUrl}
                style={{ flex: 1 }}
              />
            </Tooltip>

            <Tooltip title="Transcript">
              <Button
                icon={<ReadOutlined />}
                onClick={() => setTranscriptJob(job)}
                disabled={!job.tasks?.create_script?.script_text}
                style={{ flex: 1 }}
              />
            </Tooltip>

            <Tooltip title={hasShares ? "Copy Link" : "Share first to get a link"}>
              <Button
                icon={<CopyOutlined />}
                onClick={() => handleCopyLink(job)}
                disabled={!hasShares}
                style={{ flex: 1 }}
              />
            </Tooltip>

            <Tooltip title={hasShares ? "Analytics" : "Share first to see analytics"}>
              <Button
                icon={<BarChartOutlined />}
                onClick={() => hasShares && setAnalyticsToken(job.shares![0].share_token)}
                disabled={!hasShares}
                style={{ flex: 1 }}
              />
            </Tooltip>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto', padding: isMobile ? '16px' : '24px' }}>
      {/* Header — on mobile the search row drops below the title and fills
          the full width so the input doesn't get jammed at 200px wide. */}
      <div
        style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
          justifyContent: 'space-between',
          marginBottom: isMobile ? '16px' : '24px',
          gap: isMobile ? '12px' : '16px',
        }}
      >
        <h1
          style={{
            fontSize: isMobile ? '20px' : '24px',
            fontWeight: 600,
            margin: 0,
            color: '#1f2937',
          }}
        >
          My Reviews
        </h1>

        <div
          style={{
            display: 'flex',
            gap: '8px',
            width: isMobile ? '100%' : 'auto',
          }}
        >
          <Search
            placeholder="Search reviews..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ flex: isMobile ? 1 : '0 0 300px' }}
            allowClear
          />
          <Tooltip title="Refresh">
            <Button
              icon={<SyncOutlined spin={loading} />}
              onClick={() => fetchUserVideos()}
              disabled={loading}
            />
          </Tooltip>
        </div>
      </div>

      {/* Cards list */}
      {loading && videos.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <Spin size="large" />
        </div>
      ) : filteredVideos.length === 0 ? (
        <Empty
          description={
            searchText
              ? `No reviews found matching "${searchText}"`
              : 'No reviews generated yet'
          }
          style={{ padding: '60px 0' }}
        />
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: '16px',
        }}>
          {filteredVideos.map((job) => (
            <VideoCard key={job.job_id} job={job} />
          ))}
        </div>
      )}

      {/* Share Modal */}
      {selectedVideo && (
        <ShareModal
          visible={shareModalVisible}
          onClose={() => {
            setShareModalVisible(false);
            setSelectedVideo(null);
          }}
          jobId={selectedVideo.job_id}
          videoTitle={getDisplayTitle(selectedVideo)}
          onShareCreated={(shareUrl, shareToken) => {
            // Optionally handle share creation success
            console.log('Share created:', shareUrl);
          }}
        />
      )}

      {/* Video Player Modal */}
      <Modal
        title={selectedVideo ? getDisplayTitle(selectedVideo) : ''}
        open={playerModalVisible}
        onCancel={() => {
          setPlayerModalVisible(false);
          setSelectedVideo(null);
        }}
        footer={null}
        width={isMobile ? '95%' : '80%'}
        centered
        destroyOnClose
      >
        {selectedVideo && selectedVideo.tasks?.create_video?.video_url && (
          <VideoPlayer
            videoUrl={selectedVideo.tasks.create_video.video_url}
            title={selectedVideo.original_filename}
            createdAt={selectedVideo.created_at}
            jobId={selectedVideo.job_id}
            hideTitle={true}
          />
        )}
      </Modal>

      {/* PDF Viewer Modal */}
      <PDFViewerModal
        visible={pdfViewerState.visible}
        onClose={() => setPdfViewerState({ visible: false, pdfUrl: '', documentId: '', documentTitle: '' })}
        pdfUrl={pdfViewerState.pdfUrl}
        documentId={pdfViewerState.documentId}
        documentTitle={pdfViewerState.documentTitle}
      />

      {/* Transcript Modal */}
      <Modal
        title={transcriptJob ? `Transcript - ${getDisplayTitle(transcriptJob)}` : 'Transcript'}
        open={!!transcriptJob}
        onCancel={() => setTranscriptJob(null)}
        width={isMobile ? '95%' : '700px'}
        centered
        footer={[
          <Button
            key="download"
            type="primary"
            icon={<DownloadOutlined />}
            onClick={() => transcriptJob && handleDownloadTranscriptDocx(transcriptJob)}
          >
            Download as Word
          </Button>,
          <Button key="close" onClick={() => setTranscriptJob(null)}>Close</Button>,
        ]}
      >
        {transcriptJob?.tasks?.create_script?.script_text && (
          <div style={{
            maxHeight: '60vh',
            overflowY: 'auto',
            padding: '16px',
            backgroundColor: '#fafafa',
            borderRadius: '8px',
            fontFamily: 'Georgia, serif',
            fontSize: '14px',
            lineHeight: '1.8',
            whiteSpace: 'pre-wrap',
          }}>
            {transcriptJob.tasks.create_script.script_text
              .replace(/\[([^\]:]+)(?::\d+)?\]/g, '\n--- $1 ---\n')
              .trim()}
          </div>
        )}
      </Modal>

      {/* Share Analytics Drawer */}
      <ShareAnalyticsDrawer
        visible={!!analyticsToken}
        onClose={() => setAnalyticsToken(null)}
        shareToken={analyticsToken || ''}
      />
    </div>
  );
};

export default Videos;
